import React, { useCallback, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { LockFilled } from 'components/lib/DesignSystem/Icons'
import Tag from 'components/lib/DesignSystem/Tag'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'

import { FLYOUT_SIDE_OFFSET, navRowActive, navRowHover } from '../navStyles'

// ── Styled components ─────────────────────────────────────────────────────────

// Collapsed hover flyout (Figma 1:17505 / 1:18409). Parent section title sits at
// the top in bold; children listed below. Panel chrome + diamond connector are
// supplied by the shared V3 Tooltip primitive.
const FlyoutPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 188px;
  margin: -4px;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: var(--sidebar-item-text);
  margin-bottom: 2px;
`

// Children sit 28px in from the panel's left edge relative to the header
// (header spans full width; rows below are inset). Design feedback (Navigation
// Feedback sheet, row by Lior, 2026-08): the previous 18px inset read as
// nearly flush against the header — bumped to a clearly larger indent
// (+16px over the header's 12px). Exact px pending confirmation against the
// Figma spec (node 1:17505 / 1:18409); no file key was available in-repo to
// verify directly.
const flyoutItemBase = css`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 8px 28px;
  border-radius: 10px;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  /* Section title is bold; rows rest at regular. The active child bolds via navRowActive. */
  font-weight: 400;
  line-height: 20px;
  color: var(--sidebar-item-text);
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  border: none;
  background: transparent;
  text-align: left;
  width: 100%;
  box-sizing: border-box;

  ${navRowHover}

  /* Active child (Figma): 3px×20px blue left bar + bold, no bg fill, inset
     4px from the row's left edge — same as every other nav surface's bar. */
  ${({ $active }) => $active && navRowActive()}

  ${({ $locked, $lockedInert }) =>
    $locked &&
    css`
      gap: 6px;
      color: var(--t-colors-gray-600);
      /* pointer only when the locked row actually routes somewhere (PLG) */
      cursor: ${$lockedInert ? 'default' : 'pointer'};
    `}

  ${({ $comingSoon }) =>
    $comingSoon &&
    css`
      color: var(--sub-text-color);
      cursor: default;

      &:hover {
        background-color: transparent;
        color: var(--sub-text-color);
      }
    `}
`

// styled-components v4 does not strip `$`-prefixed transient props on a custom
// component; filter them before they reach react-router's Link DOM <a>.
const FilteredLink = ({ $active, $locked, $comingSoon, ...rest }) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Link {...rest} />
)

const FlyoutLink = styled(FilteredLink)`
  ${flyoutItemBase}
`

const FlyoutExternalLink = styled.a`
  ${flyoutItemBase}
`

const FlyoutSpan = styled.span`
  ${flyoutItemBase}
`

// Locked flyout items TRAIL a bare padlock at the right edge — grey, never blue.
// (Design review 2026-08: the padlock always sits on the right, matching the
// expanded rail's LockedNavItem, so lock placement reads the same everywhere;
// margin-left:auto pushes it past the label to the row's right edge.)
const LockBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: auto;

  svg,
  svg path {
    fill: var(--t-colors-gray-600);
  }
`

const BellDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary);
  flex-shrink: 0;
`

const BellWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
`

const FlyoutLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const HeaderTitle = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SoonTag = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--sub-text-color);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 1px 5px;
`

// Invisible hover bridge so the flyout doesn't close when moving mouse from trigger to panel
const TriggerArea = styled.div`
  position: relative;
`

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * FlyoutChild — one row inside the collapsed-rail hover flyout.
 *
 * Locked children render as a clickable row that routes to the PLG page (no
 * separate LockedNavItem tooltip inside the flyout — the flyout itself is the
 * single popup; AC "single popup" means we don't nest a second hover surface).
 * Coming-soon children render inert with a "Soon" tag, matching NavPage.
 */
const NotificationDot = () => (
  <BellWrapper aria-label="Has notifications">
    <BellDot aria-hidden="true" />
  </BellWrapper>
)

const FlyoutChild = ({ child }) => {
  const history = useHistory()
  const bell = child.hasBellNotification ? <NotificationDot /> : null

  if (child.comingSoon) {
    return (
      <FlyoutSpan as="span" $comingSoon aria-disabled="true">
        <FlyoutLabel>{child.label}</FlyoutLabel>
        <SoonTag aria-label="Coming soon">Soon</SoonTag>
      </FlyoutSpan>
    )
  }

  if (child.locked) {
    const handleLockedClick = (e) => {
      e.preventDefault()
      if (child.plg?.path) history.push(child.plg.path)
    }

    return (
      <FlyoutSpan
        as="button"
        type="button"
        data-pendo-feature={`nav-upsell-flyout-${child.id}`}
        $locked
        $lockedInert={!child.plg?.path}
        onClick={handleLockedClick}
        aria-label={`${child.label} — Upgrade to unlock this feature`}
      >
        <FlyoutLabel>{child.label}</FlyoutLabel>
        <LockBadge aria-hidden="true">
          <LockFilled noDefaultFill width="14px" height="14px" />
        </LockBadge>
      </FlyoutSpan>
    )
  }

  if (!child.resolvedPath) return null

  if (child.external) {
    return (
      <FlyoutExternalLink
        href={child.resolvedPath}
        target={child.newTab ? '_blank' : '_self'}
        rel="noopener noreferrer"
        data-pendo-feature={`nav-flyout-page-${child.id}`}
        $active={child.active}
      >
        <FlyoutLabel>{child.label}</FlyoutLabel>
        {bell}
      </FlyoutExternalLink>
    )
  }

  return (
    <FlyoutLink
      to={child.resolvedPath}
      data-pendo-feature={`nav-flyout-page-${child.id}`}
      $active={child.active}
    >
      <FlyoutLabel>{child.label}</FlyoutLabel>
      {child.tag && <Tag type={child.tag} style={{ marginLeft: '8px' }} />}
      {bell}
    </FlyoutLink>
  )
}

/**
 * NavFlyout — collapsed-state hover flyout (AC3).
 *
 * Opens after 150ms hover delay, shows parent label on top + visible children below.
 * Click on any child navigates. DISABLED (renders children directly) when expanded.
 *
 * Built on V3 Tooltip as the positioning primitive.
 *
 * @param {object} props
 * @param {boolean} props.collapsed - when false the flyout is disabled
 * @param {string} props.label - parent section label shown at top of flyout
 * @param {Array} [props.children] - decorated child nodes (visible ones rendered)
 * @param {boolean} [props.active] - parent active state
 * @param {string} [props.resolvedPath] - for direct-page sections: flyout navigates here
 * @param {string} [props.sectionId] - parent section id, for the header's Pendo Feature tag
 * @param {boolean} [props.hasBellNotification] - open-incident dot on the section title
 * @param {React.ReactNode} props.trigger - the icon-only row element rendered as trigger
 */
const NavFlyout = ({
  collapsed,
  label,
  children,
  active,
  resolvedPath,
  sectionId,
  hasBellNotification,
  trigger,
}) => {
  // Controlled open state so the flyout behaves like a menu: selecting a row (or
  // clicking the section icon, which navigates) DISMISSES it instead of leaving
  // it hovered-open under the still-resting pointer ("the hover stays also after
  // click"). Base UI opens on hover/focus via onOpenChange; we mirror that, but
  // once a click dismisses it we suppress re-opening until the pointer actually
  // leaves the trigger and comes back.
  const [open, setOpen] = useState(false)
  const suppressRef = useRef(false)
  const rearmTimer = useRef(null)

  const handleOpenChange = useCallback((next) => {
    if (next && suppressRef.current) return
    setOpen(next)
  }, [])

  // Icon click: the pointer stays resting on the trigger, and Base UI keeps
  // wanting it open — so suppress reopening until the pointer actually leaves
  // the trigger (onPointerLeave re-arms). Otherwise it'd immediately pop back.
  const dismissFromTrigger = useCallback(() => {
    suppressRef.current = true
    setOpen(false)
  }, [])

  // Flyout-child click: navigates and leaves the pointer over the (now
  // unmounting) portaled panel — NOT the trigger — so pointerleave won't fire.
  // A short guard swallows Base UI's popup-hover reopen during unmount, then
  // re-arms so a fresh hover over the icon can reopen it.
  const dismissFromFlyout = useCallback((e) => {
    e.stopPropagation()
    suppressRef.current = true
    setOpen(false)
    if (rearmTimer.current) clearTimeout(rearmTimer.current)
    rearmTimer.current = setTimeout(() => {
      suppressRef.current = false
    }, 200)
  }, [])

  const rearm = useCallback(() => {
    if (rearmTimer.current) clearTimeout(rearmTimer.current)
    suppressRef.current = false
  }, [])

  // AC3: disabled when not collapsed
  if (!collapsed) {
    return trigger
  }

  const visibleChildren = Array.isArray(children) ? children.filter((c) => c.visible) : []

  const flyoutContent = (
    // Any activation inside the panel selects → dismiss the menu (and don't let
    // the click bubble to the trigger's handler, which suppresses differently).
    <FlyoutPanel
      id={sectionId ? `nav-flyout-${sectionId}` : undefined}
      role="menu"
      aria-label={`${label} submenu`}
      onClick={dismissFromFlyout}
    >
      <HeaderRow>
        {resolvedPath ? (
          <FlyoutLink
            to={resolvedPath}
            data-pendo-feature={sectionId ? `nav-flyout-section-${sectionId}` : undefined}
            $active={active}
            style={{ fontWeight: 700 }}
          >
            <FlyoutLabel>{label}</FlyoutLabel>
          </FlyoutLink>
        ) : (
          <HeaderTitle>{label}</HeaderTitle>
        )}
        {hasBellNotification ? <NotificationDot /> : null}
      </HeaderRow>
      {visibleChildren.map((child) => (
        <FlyoutChild key={child.id} child={child} />
      ))}
    </FlyoutPanel>
  )

  return (
    // onClick here fires for trigger (icon) clicks — which navigate — so those
    // dismiss too; onPointerLeave re-arms opening once the pointer has left.
    <TriggerArea onClick={dismissFromTrigger} onPointerLeave={rearm}>
      <Tooltip
        open={open}
        onOpenChange={handleOpenChange}
        content={flyoutContent}
        side="right"
        align="start"
        delay={150}
        closeDelay={100}
        arrow
        connector="diamond"
        surface="elevated"
        /* Figma: the flyout sits a touch right of the rail with the diamond
           floating in the gap (not overlapping the rail). Shared across all
           collapsed flyouts so the diamond is the same distance from the rail
           everywhere (main nav, Settings, locked). */
        sideOffset={FLYOUT_SIDE_OFFSET}
      >
        {trigger}
      </Tooltip>
    </TriggerArea>
  )
}

export default NavFlyout
